import { ApolloError } from "@apollo/client";
import { useTranslate } from "@tolgee/react";

// Integration mutations reject invalid settings with a serialized issue
// array (code/path/message per issue). Those must never reach the snackbar:
// they echo the received values and schema internals. Human-readable backend
// messages (plain strings, or GraphQL error arrays without issue shapes)
// still pass through.

function isValidationIssue(item: unknown): boolean {
  return !!item && typeof item === "object" && "code" in item && "path" in item;
}

// API-client errors can stringify the whole request/response exchange into
// their message (e.g. graphql-request's ClientError: `Internal server
// error: {"response":{...},"request":{"query":...}}`) — an embedded JSON
// dump or an implausible length marks a message as not human-readable
function looksLikeClientDump(message: string): boolean {
  return (
    /"(response|request|errors)"\s*:/.test(message) || message.length > 300
  );
}

function containsValidationIssues(value: unknown): boolean {
  return Array.isArray(value) && value.some(isValidationIssue);
}

// The human-readable text of a thrown value. A plain object stringifies to
// "[object Object]" (under the length cap, not JSON), which would otherwise be
// shown verbatim — extract a string `message` when present, otherwise return
// "" so the caller falls back. Scalars keep their String() form.
function errorText(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  if (e !== null && typeof e === "object") {
    const message = (e as Record<string, unknown>).message;
    return typeof message === "string" ? message : "";
  }
  return String(e);
}

export function integrationErrorMessage(e: unknown, fallback: string): string {
  const raw = errorText(e);
  if (!raw) return fallback;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Not JSON — human-readable unless it embeds a client dump
    return looksLikeClientDump(raw) ? fallback : raw;
  }

  if (containsValidationIssues(parsed)) return fallback;

  if (Array.isArray(parsed)) {
    const messages: string[] = [];
    for (const item of parsed) {
      if (item && typeof item === "object" && "message" in item) {
        const message = String((item as Record<string, unknown>).message);
        try {
          // A GraphQL error whose message is itself a serialized issue array
          if (containsValidationIssues(JSON.parse(message))) return fallback;
        } catch {
          // not nested JSON — keep as-is
        }
        messages.push(message);
      }
    }
    if (messages.length === 0) return fallback;
    const joined = messages.join(", ");
    return looksLikeClientDump(joined) ? fallback : joined;
  }

  // Serialized object/scalar — nothing human-readable to show
  return fallback;
}

// Stable GraphQL error codes (extensions.code) that plugins throw instead of
// a raw vendor/Temporal message (MEIS-13089). Only ApolloError carries a
// structured extensions object; anything else has no code to read.
function getGraphQLErrorCode(e: unknown): string | undefined {
  if (!(e instanceof ApolloError)) return undefined;
  const code = e.graphQLErrors[0]?.extensions?.["code"];
  return typeof code === "string" ? code : undefined;
}

/**
 * Like `integrationErrorMessage`, but first checks the thrown error for a
 * known, stable GraphQL error code and — if found — returns a translated,
 * actionable message instead of ever looking at the (possibly raw vendor)
 * server text. Unknown codes and code-less errors fall back to
 * `integrationErrorMessage`. Add future codes to `codeMessages`.
 */
export function useIntegrationErrorMessage() {
  const { t } = useTranslate(["Integrations"]);

  const codeMessages: Record<string, string> = {
    PDS_SYNC_NOT_RUNNING: t(
      "The sync is not running. You can start it again with the sync buttons on this page.",
      { ns: "Integrations" }
    ),
  };

  return (e: unknown, fallback: string): string => {
    const code = getGraphQLErrorCode(e);
    if (code && code in codeMessages) return codeMessages[code];
    return integrationErrorMessage(e, fallback);
  };
}
